import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, Clock, Edit, ShieldCheck, Trash2 } from 'lucide-react';
import AdminNavbar from './AdminNavbar';

const ReviewPosts = ({ token, setAuthToken }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/backend/api/admin/posts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setPosts(response.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id) => {
    if (!window.confirm("Are you sure you want to publish this post live?")) return;
    try {
      await axios.put(`${process.env.REACT_APP_BASE_URL}/backend/api/admin/posts/${id}/approve`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      alert('Post approved and published!');
      fetchPosts(); 
    } catch (err) {
      console.error(err);
      alert('Failed to approve post.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("WARNING: Are you sure you want to permanently delete this post? This cannot be undone.")) return;
    
    try {
      await axios.delete(`${process.env.REACT_APP_BASE_URL}/backend/api/admin/posts/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Remove the deleted post instantly from the UI without reloading
      setPosts(posts.filter(post => post.id !== id));
      alert('Post deleted successfully.');
      
    } catch (err) {
      console.error(err);
      alert('Failed to delete post. ' + (err.response?.data?.message || err.message));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNavbar setAuthToken={setAuthToken} />
      
      <div className="pt-32 pb-24 container mx-auto px-6 max-w-7xl">
        <div className="mb-10">
          <h1 className="text-3xl font-heading font-black text-brand-dark flex items-center gap-3">
            <ShieldCheck className="text-brand-cyan" size={32} /> Master Content Table
          </h1>
          <p className="text-gray-500 mt-1">Review, approve, edit, and delete all website articles.</p>
        </div>

        <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-gray-500">Loading database...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-brand-dark text-white text-sm uppercase tracking-wider">
                    <th className="p-6 font-bold">Article Details</th>
                    <th className="p-6 font-bold">Author</th>
                    <th className="p-6 font-bold">Status</th>
                    <th className="p-6 font-bold text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {posts.map(post => (
                    <tr key={post.id} className="hover:bg-gray-50 transition-colors">
                      <td className="p-6">
                        <div className="font-bold text-brand-dark mb-1">{post.title}</div>
                        <div className="text-xs text-gray-400 uppercase tracking-widest">{post.category || 'Uncategorized'}</div>
                      </td>
                      <td className="p-6 text-gray-600 font-medium">{post.author_name || 'System'}</td>
                      <td className="p-6">
                        {post.status === 'published' ? (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-green-100 text-green-700 text-xs font-bold uppercase border border-green-200">
                            <CheckCircle size={14} /> Published
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-yellow-100 text-yellow-700 text-xs font-bold uppercase border border-yellow-200">
                            <Clock size={14} /> Pending
                          </span>
                        )}
                      </td>
                      <td className="p-6 text-right space-x-3 whitespace-nowrap">
                        
                        <Link 
                          to={`/admin/edit-post/${post.id}`}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 font-bold rounded-lg hover:bg-gray-200 transition-colors text-sm"
                        >
                          <Edit size={16} /> Edit
                        </Link>
                        
                        {post.status === 'pending' && (
                          <button 
                            onClick={() => handleApprove(post.id)}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-brand-cyan text-brand-dark font-bold rounded-lg hover:bg-brand-blue hover:text-white transition-colors text-sm shadow-sm"
                          >
                            <CheckCircle size={16} /> Approve
                          </button>
                        )}

                        <button 
                          onClick={() => handleDelete(post.id)}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 font-bold rounded-lg hover:bg-red-600 hover:text-white transition-colors text-sm"
                          title="Delete Post"
                        >
                          <Trash2 size={16} /> Delete
                        </button>

                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReviewPosts;